import {Box, Typography} from '@mui/material';
import {GridRenderCellParams} from '@mui/x-data-grid';
import {t} from 'i18next';
import {
  Fragment,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import ReactDOM from 'react-dom';
import {useSelector} from 'react-redux';
import {ReactComponent as AddIcon} from '../../../../../../assets/images/add.svg';

import {useGridApiRef} from '@mui/x-data-grid';
import {
  IDocumentRedaction,
  RedactionTags,
} from '../../../../../../api/types/document.type';
import {useGetRedactionTags} from '../../../../../../hooks/document/useDocument';
import {
  useDocumentMediaAddEditRedaction,
  useDocumentMediaDeleteRedaction,
  useDocumentMediaRedactions,
} from '../../../../../../hooks/document/useDocumentMediaRedactions';
import {RootState} from '../../../../../../redux/store';
import {ToastType} from '../../../../../../shared/constants';
import {isEmpty} from '../../../../../../utils/formatters';
import {durationToSeconds} from '../../../../../../utils/utils';
import ArrDataGrid from '../../../../../common/arr-data-grid/ArrDataGrid';
import ArrIconButton from '../../../../../common/arr-icon-button/ArrIconButton';
import ArrLoadingSpinner from '../../../../../common/arr-loading-spinner/ArrLoadingSpinner';
import ArrModal from '../../../../../common/arr-modal/ArrModal';
import ArrToastNotification, {
  ArrStatusToastProps,
} from '../../../../../common/arr-toast-notification/ArrToastNotification';
import {ArrAVPreviewContext} from '../../../../../components/avdoc-preview/arr-av-provider/ArrAVPreviewProvider';
import {
  DocRedactionPressType,
  useDocRedactionsColumns,
} from './useDocRedactionsColumns';
import './DocumentMediaRedactions.scss';

const DURATION_REGEX = /^([0-9]{1,5}):([0-5][0-9]):([0-5][0-9])$/;

const DocumentMediaRedactions: React.FC = () => {
  const activeDocument = useSelector(
    (state: RootState) => state.docView.activeDoc
  );

  const audioDocumentId = useMemo(
    () => parseInt(activeDocument?.documentId ?? ''),
    [activeDocument?.documentId]
  );

  if (isEmpty(audioDocumentId)) {
    return <Fragment />;
  }

  const avPreviewcontext = useContext(ArrAVPreviewContext);
  const {
    addRegionsFromRedactions,
    isPlayerReady,
    playRegion,
    clearAllRegions,
    stop,
  } = avPreviewcontext;

  const apiRef = useGridApiRef();
  const {DocRedactionsColumns} = useDocRedactionsColumns();

  const {annotationLayerId: annotationLayerID} = useSelector(
    (state: RootState) => state.docView.docViewConfiguration
  );

  const [editRedaction, setEditRedaction] = useState<IDocumentRedaction>();
  const addRedactionRef = useRef<IDocumentRedaction>(undefined);
  const [redactionList, setRedactionList] = useState<IDocumentRedaction[]>([]);
  const [redactionMasterList, setRedactionMasterList] = useState<
    IDocumentRedaction[]
  >([]);

  const [showToast, setShowToast] = useState<boolean>(false);
  const [toastMessage, setToastMessage] = useState<string>('');
  const [toastType, setToastType] = useState(ToastType.Success);
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [prevSelectedRedactionTagId, setPrevSelectedRedactionTagId] =
    useState<number>();

  const [isSortable, setIsSortable] = useState(true);

  const mediaDuration = useMemo(
    () => Math.floor(parseInt(activeDocument?.miniMediaLength ?? '0') / 1000),
    [activeDocument?.miniMediaLength]
  );

  const {mutate: getAudioRedactions, isPending: getRedactionLoading} =
    useDocumentMediaRedactions();

  const {
    data: redactionTagsData,
    isLoading: getRedactionTagsLoading,
    refetch: getRedationTags,
  } = useGetRedactionTags(false);

  const {mutate: addAudioRedaction, isPending: addEditRedactionLoading} =
    useDocumentMediaAddEditRedaction();

  const {mutate: deleteAudioRedaction, isPending: deleteRedactionLoading} =
    useDocumentMediaDeleteRedaction();

  const fetchRedactionList = useCallback(
    (docID: number) => {
      getAudioRedactions(
        {audioDocumentId: docID},
        {
          onSuccess: (data: IDocumentRedaction[]) => {
            addRedactionRef.current = undefined;
            setEditRedaction(undefined);
            setRedactionList(data ?? []);
            setRedactionMasterList(data ?? []);
          },
          onError: (error) => {
            setToastMessage(error?.message);
            setToastType(ToastType.Error);
            setShowToast(true);
          },
        }
      );
    },
    [getAudioRedactions]
  );

  useEffect(() => {
    if (!isEmpty(audioDocumentId)) {
      clearAllRegions();
      getRedationTags();
      fetchRedactionList(audioDocumentId);
    }
  }, [audioDocumentId, clearAllRegions, fetchRedactionList, getRedationTags]);

  useEffect(() => {
    if (isPlayerReady) {
      clearAllRegions();
      addRegionsFromRedactions(redactionMasterList ?? []);
    }
  }, [
    addRegionsFromRedactions,
    clearAllRegions,
    isPlayerReady,
    redactionMasterList,
  ]);

  const isNoData = useMemo(
    () => redactionList?.length <= 0,
    [redactionList?.length]
  );

  const isRedactionsOverlap = useCallback(
    (redaction: IDocumentRedaction) => {
      const {
        startTime = '',
        endTime = '',
        documentAudioRedactionID,
      } = redaction;

      const isOverlap = redactionMasterList.some((item: IDocumentRedaction) => {
        const {
          startTime: redactionStartTime,
          endTime: redactionEndTime,
          documentAudioRedactionID: redactionID,
        } = item;

        return (
          !(
            (durationToSeconds(startTime) <
              durationToSeconds(redactionStartTime) &&
              durationToSeconds(endTime) <
                durationToSeconds(redactionStartTime)) ||
            (durationToSeconds(startTime) >
              durationToSeconds(redactionEndTime) &&
              durationToSeconds(endTime) > durationToSeconds(redactionEndTime))
          ) && documentAudioRedactionID !== redactionID
        );
      });

      return isOverlap;
    },
    [redactionMasterList]
  );

  const isValidDuration = useCallback(
    (redaction: IDocumentRedaction) => {
      const {startTime = '', endTime = ''} = redaction;

      let isValid =
        DURATION_REGEX.test(startTime) && DURATION_REGEX.test(endTime);
      let errorMessage = '';

      if (!isValid) {
        errorMessage = t('docRedactions.errors.invalidStartEndTime');
      } else if (startTime === endTime) {
        isValid = false;
        errorMessage = t('docRedactions.errors.startEndTimeSame');
      } else if (durationToSeconds(startTime) > durationToSeconds(endTime)) {
        isValid = false;
        errorMessage = t('docRedactions.errors.startTimeGreaterEndTime');
      } else if (durationToSeconds(endTime) > mediaDuration) {
        isValid = false;
        errorMessage = t('docRedactions.errors.endTimeGreaterActualDuration');
      } else if (isRedactionsOverlap(redaction)) {
        isValid = false;
        errorMessage = t('docRedactions.errors.redactionRangeOverlap');
      }
      if (!isValid) {
        setToastMessage(errorMessage);
        setToastType(ToastType.Error);
        setShowToast(true);
      }
      return isValid;
    },
    [isRedactionsOverlap, mediaDuration]
  );

  const addRedaction = useCallback(
    (redaction: IDocumentRedaction) => {
      const redactionID = redaction.documentAudioRedactionID;
      const redactionTagID = redaction.audioRedactionIDs;
      addAudioRedaction(
        {
          ...redaction,
          documentID: audioDocumentId,
          redactionWidth: '0',
          redActionLeftMargin: '0',
          annotationLayerID: parseInt(annotationLayerID ?? '0'),
        },
        {
          onSuccess: () => {
            setPrevSelectedRedactionTagId(parseInt(redactionTagID ?? '0'));
            fetchRedactionList(audioDocumentId);
            setToastMessage(
              redactionID === 0
                ? t('docRedactions.recordAdded')
                : t('docRedactions.recordUpdated')
            );
            setToastType(ToastType.Success);
            setShowToast(true);
          },
          onError: (error) => {
            setToastMessage(error?.message);
            setToastType(ToastType.Error);
            setShowToast(true);

            addRedactionRef.current = undefined;
            setEditRedaction(undefined);
            fetchRedactionList(audioDocumentId);
          },
        }
      );
    },
    [addAudioRedaction, annotationLayerID, audioDocumentId, fetchRedactionList]
  );

  const deleteRedaction = useCallback(
    (redaction: IDocumentRedaction) => {
      const reductionTag = (redactionTagsData?.redactionTagDetails ?? []).find(
        (item: RedactionTags) => item.name === redaction.redactionLabels
      );

      deleteAudioRedaction(
        {
          ...redaction,
          audioRedactionIDs: reductionTag?.redactionID?.toString(),
          redactionLabels: reductionTag?.redactionID?.toString(),
          annotationLayerID: parseInt(annotationLayerID ?? '0'),
        },
        {
          onSuccess: () => {
            setShowDeleteModal(false);
            fetchRedactionList(audioDocumentId);
            addRedactionRef.current = undefined;
            setToastMessage(t('docRedactions.recordDeleted'));
            setToastType(ToastType.Success);
            setShowToast(true);
          },
          onError: (error) => {
            addRedactionRef.current = undefined;
            setToastMessage(error?.message);
            setToastType(ToastType.Error);
            setShowToast(true);
          },
        }
      );
    },
    [
      annotationLayerID,
      audioDocumentId,
      deleteAudioRedaction,
      fetchRedactionList,
      redactionTagsData?.redactionTagDetails,
    ]
  );

  const deleteNewRedactionRow = useCallback(() => {
    setIsSortable(true);
    if (addRedactionRef.current?.documentAudioRedactionID === 0) {
      const updatedRedactionList = [...redactionList];
      updatedRedactionList.shift();
      setRedactionList(updatedRedactionList);
      addRedactionRef.current = undefined;
    }
  }, [redactionList, setIsSortable]);

  const handleAdd = useCallback(() => {
    apiRef.current?.setSortModel([]);
    setIsSortable(false);
    if (!addRedactionRef.current) {
      setEditRedaction(undefined);
      addRedactionRef.current = {documentAudioRedactionID: 0};
      const updateList = [addRedactionRef.current, ...redactionList];
      setRedactionList(updateList);
    }
  }, [apiRef, redactionList, setIsSortable]);

  const handleActionPress = useCallback(
    (
      type: DocRedactionPressType,
      params: GridRenderCellParams<IDocumentRedaction>,
      updatedRedaction?: Partial<IDocumentRedaction>
    ) => {
      const redactionID = params.row.documentAudioRedactionID.toString();
      switch (type) {
        case 'save': {
          const reduction = {...params.row, ...updatedRedaction};
          if (isValidDuration(reduction)) {
            addRedaction(reduction);
          }
          break;
        }
        case 'edit': {
          deleteNewRedactionRow();
          setEditRedaction({...params.row});
          break;
        }
        case 'close': {
          deleteNewRedactionRow();
          setEditRedaction(undefined);
          setRedactionList([...redactionMasterList]);
          break;
        }
        case 'remove': {
          deleteNewRedactionRow();
          setEditRedaction(undefined);
          setRedactionList([...redactionMasterList]);
          addRedactionRef.current = {...params.row};
          setShowDeleteModal(true);
          break;
        }
        case 'play': {
          playRegion(redactionID);
          break;
        }
        case 'stop': {
          stop();
          break;
        }
      }
    },
    [
      addRedaction,
      deleteNewRedactionRow,
      isValidDuration,
      playRegion,
      redactionMasterList,
      stop,
    ]
  );

  const redactionColumns = useMemo(
    () =>
      DocRedactionsColumns({
        apiRef,
        prevSelectedRedactionTagId,
        editRedaction,
        handleActionPress,
        redactionTags: redactionTagsData?.redactionTagDetails ?? [],
        isSortable,
      }),
    [
      DocRedactionsColumns,
      apiRef,
      prevSelectedRedactionTagId,
      editRedaction,
      handleActionPress,
      isSortable,
      redactionTagsData?.redactionTagDetails,
    ]
  );

  const NoRowsOverlay = useCallback(
    () =>
      !getRedactionLoading ? (
        <Box className="no-grid-overlay">
          <Typography
            className="typo-text body3_size nodata_label"
            data-testid={`document-media-redactions-message-for-no-grid-data`}>
            {t('docRedactions.messageForNoGridData')}
          </Typography>
        </Box>
      ) : (
        <Box />
      ),
    [getRedactionLoading]
  );

  const RedactionToastNotification = useCallback(
    (props: ArrStatusToastProps) => {
      return ReactDOM.createPortal(
        <ArrToastNotification {...props} />,
        document.body
      );
    },
    []
  );

  return (
    <div
      className="docs-media-redactions"
      data-testid={'redactions-list-grid-table'}
    >
      <Box className={'header-container'}>
        <Typography
          className="typo-text subhead5_size title"
          data-testid={`document-media-redactions-redaction`}>
          {t('docRedactions.redaction')}
        </Typography>
        <ArrIconButton
          icon={<AddIcon className="add-icon" />}
          onClick={handleAdd}
          ariaLabel={t('assignment.dataGrid.showHideColumns')}
          shape="none"
          size="small"
          testId="document-media-redactions-show-hide-columns-icon-button"
        />
      </Box>
      <ArrDataGrid
        apiRef={apiRef}
        getRowId={(row: IDocumentRedaction) =>
          row.documentAudioRedactionID.toString()
        }
        loading={false}
        columns={redactionColumns}
        rows={redactionList}
        className={
          isNoData
            ? 'redactions-list-grid no-grid-overlay-height'
            : 'redactions-list-grid'
        }
        getRowHeight={() => 'auto'}
        primaryKey={'documentAudioRedactionID'}
        noRowsOverlay={NoRowsOverlay}
        hideFooter
        hideFooterPagination
        disableVirtualization
      />
      <ArrModal
        showModal={showDeleteModal}
        ariaLabel={t('docRedactions.deleteRedactionModal.deleteRedaction')}
        title={t('docRedactions.deleteRedactionModal.deleteRedaction')}
        ariaDescription={t(
          'docRedactions.deleteRedactionModal.sureWantToDelete'
        )}
        description={t('docRedactions.deleteRedactionModal.sureWantToDelete')}
        onClose={() => setShowDeleteModal(false)}
        autoFocusCloseButton={true}
        dataTestId="document-media-redactions-modal"
        buttons={[
          {
            id: 'no-button',
            label: t('common.no'),
            dataTestId: 'dmr-no-button',
            onClick: () => {
              addRedactionRef.current = undefined;
              setShowDeleteModal(false);
            },
            variant: 'secondary',
            disabled: deleteRedactionLoading,
          },
          {
            id: 'yes-button',
            label: t('common.yes'),
            dataTestId: 'dmr-yes-button',
            onClick: () => deleteRedaction(addRedactionRef.current!),
            variant: 'primary',
            loading: deleteRedactionLoading,
            disabled: deleteRedactionLoading,
          },
        ]}
      />
      {showToast && (
        <RedactionToastNotification
          message={toastMessage}
          onClose={() => setShowToast(false)}
          status={[toastType]}
        />
      )}
      <ArrLoadingSpinner
        isOpen={
          addEditRedactionLoading ||
          getRedactionLoading ||
          getRedactionTagsLoading
        }
        overlay={true}
      />
    </div>
  );
};

export {DocumentMediaRedactions};
